import Phaser from 'phaser';
import {
  CombatPosition,
  CombatStateDTO,
  EntityCombatStateDTO,
  EntityStatus,
  getMaxHP,
  getMaxMP,
  PLAYER_GROUP_ID,
} from '@hexcrawl/shared-utils';
import { applyEntityStatus } from '@/ui/stores/gameStore';
import { formatActiveEffectTag } from '@/ui/utils/activeEffectSummary';
import { getSpriteAssetKey } from '@/utils/sprites';
import { AnimatedSprite } from '@/utils/AnimatedSprite';
import type { CombatCoordinates } from './CombatCoordinates';
import type { CombatContext } from './CombatContext';
import { DepthOrder } from './constants';

export class CombatEntityManager {
  private _entitySprites: Map<string, AnimatedSprite> = new Map();
  private _lastPositions: Map<string, CombatPosition> = new Map();
  private _dyingEntities: Set<string> = new Set();
  private _predictiveMoveEntities: Set<string> = new Set();

  get sprites(): Map<string, AnimatedSprite> {
    return this._entitySprites;
  }

  get dyingEntities(): Set<string> {
    return this._dyingEntities;
  }

  addPredictiveMoveEntity(entityId: string): void {
    this._predictiveMoveEntities.add(entityId);
  }

  removePredictiveMoveEntity(entityId: string): void {
    this._predictiveMoveEntities.delete(entityId);
  }

  hasPredictiveMoveEntity(entityId: string): boolean {
    return this._predictiveMoveEntities.has(entityId);
  }

  initEntities(scene: Phaser.Scene, combat: CombatStateDTO, coords: CombatCoordinates, context: CombatContext): void {
    for (const entityState of combat.entityStates) {
      if (entityState.status !== EntityStatus.DEAD) {
        this.createEntitySprite(scene, entityState, coords, context);
        this._lastPositions.set(entityState.entityId, { ...entityState.position });
      }
    }
  }

  createEntitySprite(scene: Phaser.Scene, entityState: EntityCombatStateDTO, coords: CombatCoordinates, context: CombatContext): void {
    const worldPos = coords.tileToWorld(entityState.position.x + 0.5, entityState.position.y + 0.5);
    if (!worldPos) return;

    const char = context.roster[entityState.entityId];
    if (!char) {
      console.warn(`[CombatEntityManager] No hydrated character for entity ${entityState.entityId}`);
      return;
    }

    const isPlayer = entityState.groupId === PLAYER_GROUP_ID;
    const spriteRef = char.sprite;

    // Composite sprites have spriteHash but no sprite zip filename.
    // Each entity's texture is keyed as composite-<entityId>, loaded by CombatScene.loadRosterSprites.
    const textureKey = spriteRef ? getSpriteAssetKey(spriteRef) : `composite-${entityState.entityId}`;
    if (!spriteRef && !char.spriteHash) {
      console.warn(`[CombatEntityManager] Entity ${entityState.entityId} (${char.name}) has no sprite — skipping`);
      return;
    }
    const texture = scene.textures.get(textureKey);
    if (!texture) {
      throw new Error(`Missing sprite texture for entity ${entityState.entityId} (key: ${textureKey})`);
    }

    console.log(`Creating sprite with texture: ${textureKey}`);
    const phaserSprite = scene.add.sprite(worldPos.x, worldPos.y, textureKey);
    phaserSprite.setScale(0.3);
    phaserSprite.setOrigin(0.5, 1);

    const animatedSprite = new AnimatedSprite(phaserSprite, textureKey);
    this._entitySprites.set(entityState.entityId, animatedSprite);
    animatedSprite.setDepth(this.getIsoDepth(worldPos));

    const enemyState = context.combat.enemyStates.find(e => e.entityId === entityState.entityId);
    const displayName = enemyState?.name ?? char.name ?? '?';
    animatedSprite.enableNameTag({
      text: displayName,
      fontSize: '24px',
      color: isPlayer ? '#a0ffb0' : '#ffb0a0',
      yOffset: 5,
    });

    const maxHP = getMaxHP(char);
    const maxMP = getMaxMP(char);
    const { currentHP, currentMP } = context.getEntityVitals(entityState, maxHP, maxMP);
    animatedSprite.enableStatBars({ maxHP, maxMP, currentHP, currentMP });
    // Resuming a saved game mid-combat must show buffs that were already running.
    animatedSprite.setBuffTag(formatActiveEffectTag(entityState.activeEffects, context.combat.round));
  }

  sync(scene: Phaser.Scene, coords: CombatCoordinates, context: CombatContext): void {
    const { combat, battlefield } = context;

    for (const entityState of combat.entityStates) {
      if (entityState.status === EntityStatus.DEAD) continue;

      const sprite = this._entitySprites.get(entityState.entityId);
      const entity = context.roster[entityState.entityId];
      if (!sprite || !entity) continue;

      const lastPosition = this._lastPositions.get(entityState.entityId);
      const positionChanged = lastPosition
        && (lastPosition.x !== entityState.position.x || lastPosition.y !== entityState.position.y);
      if (positionChanged) {
        sprite.setFacing(coords.getFacingBetweenPositions(lastPosition, entityState.position));
      }

      const worldPos = coords.tileToWorld(entityState.position.x + 0.5, entityState.position.y + 0.5);
      if (worldPos && !this._predictiveMoveEntities.has(entityState.entityId)) {
        if (sprite.x !== worldPos.x || sprite.y !== worldPos.y) {
          sprite.playAnimation('walk');
          scene.tweens.add({
            targets: sprite,
            x: worldPos.x,
            y: worldPos.y,
            duration: battlefield.distanceBetween({ x: sprite.x, y: sprite.y }, worldPos) * 25,
            ease: 'Sine.easeOut',
            onComplete: () => {
              sprite.playAnimation('idle');
            },
          });
        }
      }

      sprite.updateOverlayPositions();
      sprite.setDepth(this.getIsoDepth({ x: sprite.x, y: sprite.y }));

      const maxHP = getMaxHP(entity);
      const maxMP = getMaxMP(entity);
      const { currentHP, currentMP } = context.getEntityVitals(entityState, maxHP, maxMP);
      sprite.setVitals(currentHP, currentMP);
      sprite.setBuffTag(formatActiveEffectTag(entityState.activeEffects, combat.round));
    }

    this._lastPositions.clear();
    for (const entityState of combat.entityStates) {
      if (entityState.status !== EntityStatus.DEAD) {
        this._lastPositions.set(entityState.entityId, { ...entityState.position });
      }
    }
  }

  updateDepths(getIsoDepth: (pos: { x: number; y: number }) => number): void {
    for (const sprite of this._entitySprites.values()) {
      sprite.setDepth(getIsoDepth({ x: sprite.x, y: sprite.y }));
    }
  }

  async processDeath(_scene: Phaser.Scene, entityId: string): Promise<void> {
    applyEntityStatus(entityId, EntityStatus.DEAD);

    const sprite = this._entitySprites.get(entityId);
    if (!sprite || this._dyingEntities.has(entityId)) return;

    this._dyingEntities.add(entityId);
    try {
      await sprite.playAnimation('death', { freezeAtEnd: true });
    } finally {
      sprite.destroy();
      this._entitySprites.delete(entityId);
      this._dyingEntities.delete(entityId);
    }
  }

  clear(): void {
    this._entitySprites.clear();
    this._lastPositions.clear();
    this._dyingEntities.clear();
    this._predictiveMoveEntities.clear();
  }

  private getIsoDepth(worldPos: { x: number; y: number }): number {
    return DepthOrder.SPRITES + worldPos.y - worldPos.x * 0.001;
  }
}
